import { PREDEFINED_MESSAGES_API_URL, PREDEFINED_MESSAGES_FILTER_URL, PREDEFINED_MESSAGES_SEARCH_BY_DEALER_OR_GROUP_URL, PREDEFINED_MESSAGES_URL } from 'constants/ApiConstants';

import * as actionTypes from './actionTypes';

/** @typedef {import('./types').PredefinedMessage} PredefinedMessage */

/**
 * @return {import('./types').ApplicationReadySuccessAction}
 */
export const applicationReadySuccess = () => ({
  type: actionTypes.APPLICATION_READY_SUCCESS,
});

/**
 * @param {string} error
 * @return {import('./types').ApplicationReadyFailureAction}
 */
export const applicationReadyFailure = error => ({
  type: actionTypes.APPLICATION_READY_FAILURE,
  payload: error,
  error: true,
});

/**
 * @param {string} id
 * @return {import('./types').FetchPredefinedMessageAction}
 */
export const fetchPredefinedMessage = id => ({
  type: actionTypes.FETCH_PREDEFINED_MESSAGE,
  payload: {
    request: {
      url: `${PREDEFINED_MESSAGES_URL}/${id}`,
      method: 'GET',
    },
  },
});

/**
 * @param {PredefinedMessage} predefinedMessage
 * @return {import('./types').FetchPredefinedMessageSuccessAction}
 */
export const fetchPredefinedMessageSuccess = predefinedMessage => ({
  type: actionTypes.FETCH_PREDEFINED_MESSAGE_SUCCESS,
  payload: predefinedMessage,
});

/**
 * @param {string} error
 * @return {import('./types').FetchPredefinedMessageFailureAction}
 */
export const fetchPredefinedMessageFailure = error => ({
  type: actionTypes.FETCH_PREDEFINED_MESSAGE_FAILURE,
  payload: error,
  error: true,
});

/**
 * @param {Object<string, any>} params
 * @param {Object={}} meta
 * @return {import('./types').FetchPredefinedMessagesAction}
 */
export const fetchPredefinedMessages = (params, meta) => ({
  type: actionTypes.FETCH_PREDEFINED_MESSAGES,
  payload: {
    request: {
      method: 'GET',
      url: PREDEFINED_MESSAGES_FILTER_URL,
      params: {
        page: 0,
        size: 10,
        projection: 'page',
        fields: ['!dealer', '!group'].join(),
        ...params,
      },
    },
    ...params,
  },
  meta,
});

/**
 * @param {import('./types').FetchPredefinedMessagesResponse} response
 * @param {Object={}} meta
 * @return {import('./types').FetchPredefinedMessagesSuccessAction}
 */
const fetchPredefinedMessagesSuccess = (response, meta = {}) => ({
  type: actionTypes.FETCH_PREDEFINED_MESSAGES_SUCCESS,
  payload: response,
  meta,
});

/**
 * @param {string} message
 * @param {Object={}} meta
 * @return {import('./types').FetchPredefinedMessagesFailureAction}
 */
const fetchPredefinedMessagesFailure = (message, meta = {}) => ({
  type: actionTypes.FETCH_PREDEFINED_MESSAGES_FAILURE,
  payload: message,
  error: true,
  meta,
});

/**
 * @param {Object} params
 * @param {Object={}} meta
 * @return {import('./types').FetchAllByDealerIdAction}
 */
export const fetchAllByDealerId = (params = {}, meta = {}) => ({
  ...fetchPredefinedMessages(params, meta),
  type: actionTypes.FETCH_ALL_BY_DEALER,
});

/**
 * @param {import('./types').FetchPredefinedMessagesResponse} response
 * @param {Object={}} meta
 * @return {import('./types').FetchAllByDealerIdSuccessAction}
 */
export const fetchAllByDealerIdSuccess = (response, meta = {}) => ({
  type: actionTypes.FETCH_ALL_BY_DEALER_SUCCESS,
  payload: response,
  meta,
});

/**
 * @param {string} message
 * @param {Object={}} meta
 * @return {import('./types').FetchAllByDealerIdFailureAction}
 */
export const fetchAllByDealerIdFailure = (message, meta = {}) => ({
  type: actionTypes.FETCH_ALL_BY_DEALER_FAILURE,
  payload: message,
  meta,
});

// TODO - ADD GROUP
export const genericFetchPredefinedMessagesSuccess = typeSource => {
  switch (typeSource) {
    case actionTypes.FETCH_ALL_BY_DEALER:
      return fetchAllByDealerIdSuccess;
    default:
      return fetchPredefinedMessagesSuccess;
  }
};

// TODO - ADD GROUP
export const genericFetchPredefinedMessagesFailure = typeSource => {
  switch (typeSource) {
    case actionTypes.FETCH_ALL_BY_DEALER:
      return fetchAllByDealerIdFailure;
    default:
      return fetchPredefinedMessagesFailure;
  }
};

/**
 * @param {string} dealerId
 * @param {string} groupId
 * @param {Object={}} params
 * @returns {import('./types').FetchPredefinedMessagesByDealerOrGroupAction}
 */
export const fetchFindByDealerOrGroup = (dealerId, groupId, params = {}) => ({
  type: actionTypes.FETCH_FIND_DEALER_OR_GROUP,
  payload: {
    request: {
      url: PREDEFINED_MESSAGES_SEARCH_BY_DEALER_OR_GROUP_URL,
      method: 'GET',
      params: {
        dealerId,
        groupId,
        ...params,
      },
    },
    dealerId,
    groupId,
    ...params,
  },
});

/**
 *
 * @param {import('./types').FetchPredefinedMessagesResponse} response
 * @param {Object={}} meta
 * @returns {import('./types').FetchPredefinedMessagesByDealerOrGroupSuccessAction}
 */
export const fetchFindByDealerOrGroupSuccess = (response, meta = {}) => ({
  type: actionTypes.FETCH_FIND_DEALER_OR_GROUP_SUCCESS,
  payload: response,
  meta,
});

/**
 *
 * @param {string} error
 * @returns {import('./types').FetchPredefinedMessagesByDealerOrGroupFailureAction}
 */
export const fetchFindByDealerOrGroupFailure = error => ({
  type: actionTypes.FETCH_FIND_DEALER_OR_GROUP_FAILURE,
  payload: error,
  error: true,
});

/**
 * @param {PredefinedMessage} predefinedMessage
 * @return {import('./types').PostPredefinedMessageAction}
 */
export const create = predefinedMessage => ({
  type: actionTypes.CREATE,
  payload: {
    request: {
      url: PREDEFINED_MESSAGES_API_URL,
      method: 'POST',
      data: predefinedMessage,
    },
  },
});

/**
 * @param {PredefinedMessage} predefinedMessage
 * @return {import('./types').PostPredefinedMessageSuccessAction}
 */
export const createSuccess = predefinedMessage => ({
  type: actionTypes.CREATE_SUCCESS,
  payload: predefinedMessage,
});

/**
 * @param {string} error
 * @return {import('./types').PostPredefinedMessageFailureAction}
 */
export const createFailure = error => ({
  type: actionTypes.CREATE_FAILURE,
  payload: error,
  error: true,
});

/**
 * @param {PredefinedMessage} predefinedMessage
 * @return {import('./types').PutPredefinedMessageAction}
 */
export const update = predefinedMessage => ({
  type: actionTypes.UPDATE,
  payload: {
    request: {
      url: `${PREDEFINED_MESSAGES_API_URL}/${predefinedMessage.id}`,
      method: 'PUT',
      data: predefinedMessage,
    },
  },
});

/**
 * @param {PredefinedMessage} predefinedMessage
 * @return {import('./types').PutPredefinedMessageSuccessAction}
 */
export const updateSuccess = predefinedMessage => ({
  type: actionTypes.UPDATE_SUCCESS,
  payload: predefinedMessage,
});

/**
 * @param {string} error
 * @return {import('./types').PutPredefinedMessageFailureAction}
 */
export const updateFailure = error => ({
  type: actionTypes.UPDATE_FAILURE,
  payload: error,
  error: true,
});

/**
 * @param {string} error
 * @return {import('./types').DeletePredefinedMessageActionParams}
 */
export const deletePredefinedMessage = id => ({
  type: actionTypes.DELETE,
  payload: {
    request: {
      url: `${PREDEFINED_MESSAGES_API_URL}/${id}`,
      method: 'DELETE',
    },
    id,
  },
});

/**
 * @param {string} error
 * @return {import('./types').DeletePredefinedMessageSuccessAction}
 */
export const deletePredefinedMessageSuccess = id => ({
  type: actionTypes.DELETE_SUCCESS,
  payload: id,
});

/**
 * @param {string} error
 * @return {import('./types').DeletePredefinedMessageFailureAction}
 */
export const deletePredefinedMessageFailure = error => ({
  type: actionTypes.DELETE_FAILURE,
  payload: error,
  error: true,
});

/**
 * @param {string} error
 * @return {import('./types').ClonePredefinedMessageAction}
 */
export const clonePredefinedMessage = ({ id, dealerId, groupId }) => ({
  type: actionTypes.CLONE,
  payload: {
    request: {
      url: `${PREDEFINED_MESSAGES_API_URL}/${id}/clone`,
      method: 'POST',
      data: { dealerId, groupId },
    },
  },
});

/**
 * @param {string} error
 * @return {import('./types').ClonePredefinedMessageSuccessAction}
 */
export const clonePredefinedMessageSuccess = id => ({
  type: actionTypes.CLONE_SUCCESS,
  payload: id,
});

/**
 * @param {string} error
 * @return {import('./types').ClonePredefinedMessageFailureAction}
 */
export const clonePredefinedMessageFailure = error => ({
  type: actionTypes.CLONE_FAILURE,
  payload: error,
  error: true,
});

/**
 * @param {string} predefinedMessageId
 * @param {string} id
 * @return {import('./types').SelectPredefinedMessageAction}
 */
export const selectPredefinedMessage = predefinedMessageId => ({
  type: actionTypes.SELECT_ID,
  payload: predefinedMessageId,
});

/**
 * @param {string} language
 * @return {import('./types').SelectLanguageAction}
 */
export const selectLanguage = language => ({
  type: actionTypes.SELECT_LANGUAGE,
  payload: language,
});

/**
 * @param {Array<string>} languages
 * @return {import('./types').SetLanguagesAction}
 */
export const setLanguages = languages => ({
  type: actionTypes.SET_LANGUAGES,
  payload: languages,
});

/**
 * @param {Object} filters
 * @param {Object} meta
 * @return {import('./types').SetDashboardFiltersAction}
 */
export const setDashboardFilters = (filters, meta = {}) => ({
  type: actionTypes.SET_DASHBOARD_FILTERS,
  payload: filters,
  meta,
});

/**
 * @param {string} field
 * @param {Object=} meta
 * @return {import('./types').RemoveDashboardFiltersAction}
 */
export const removeDashboardFilters = (field, meta = {}) => ({
  type: actionTypes.REMOVE_DASHBOARD_FILTERS,
  payload: field,
  meta,
});

/**
 * @param {Object={}} meta
 * @return {import('./types').RefreshDashboardAction}
 */
export const refreshDashboard = (payload = {}, meta = {}) => ({
  type: actionTypes.REFRESH_DASHBOARD,
  payload,
  meta,
});

/**
 *
 * @param {import('./types').ParamsPayload} payload
 * @param {Object} meta
 * @returns {import('./types').OpenDuplicateModalAction}
 */
export const openDuplicateModal = (payload, meta) => ({
  type: actionTypes.OPEN_DUPLICATE_MODAL,
  payload,
  meta,
});

/**
 * @returns {import('./types').CloseDuplicateModalAction}
 */
export const closeDuplicateModal = () => ({
  type: actionTypes.CLOSE_DUPLICATE_MODAL,
});

export const translationsPageReady = (meta = {}) => ({
  type: actionTypes.TRANSLATIONS_PAGE_READY,
  meta,
});
